import {useEffect, useRef} from 'react'

import {KetchConsentPurpose, onKetchConsent} from '../ketchConsent'
import {SmadexEventParams, SmadexFunnelEvent, loadSmadexPixel, trackSmadexEvent} from './smadexPixel'

// Per document rather than per mount. `onKetchConsent` has no unsubscribe, so a listener
// registered by one page keeps running after a client-side navigation away from it and would
// otherwise re-report its event against a page the user has already left.
const reportedEvents = new Set<SmadexFunnelEvent>()

/**
 * Report one Smadex funnel event for the page that mounts this, once the user has
 * consented to targeted advertising.
 *
 * Fires from script rather than from a rendered `<Script>` tag because the pages that
 * report these events are reachable both by a full document load (a magic-link
 * redirect) and by a client-side route change (SSO, which pushes after its popup
 * resolves). A `beforeInteractive` tag covers only the first, which is why SSO sign-ups
 * went unreported. Loading the pixel here rather than assuming a tag already did covers
 * the same gap for the loader itself.
 *
 * Callers that navigate away on completion must hold the redirect until
 * `smadexPixelHasLoaded`: until then the event is sitting in the loader's queue and has
 * not left the browser.
 */
export function useSmadexFunnelEvent({
  event,
  enabled,
  params
}: {
  event: SmadexFunnelEvent
  enabled: boolean
  params?: SmadexEventParams
}): void {
  // `onKetchConsent` has no unsubscribe and reports every consent state rather than
  // transitions, so this registers once and collapses repeat grants into one event.
  const argsRef = useRef({event, enabled, params})
  argsRef.current = {event, enabled, params}

  useEffect(() => {
    onKetchConsent(KetchConsentPurpose.TargetedAdvertising, () => {
      const {event, enabled, params} = argsRef.current
      if (!enabled || reportedEvents.has(event)) {
        return
      }
      reportedEvents.add(event)
      loadSmadexPixel({enabled})
      trackSmadexEvent(event, params)
    })
  }, [])
}
